import React, { useState, useEffect,useRef } from 'react';
import { getFirestore, collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import ChatModal from './ChatModal';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';


const Messages = ({ isAdmin, isWorker }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [response, setResponse] = useState('');
  const db = getFirestore();
  const auth = getAuth();
  const messagesEndRef = useRef(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {

    console.log("Messages component mounted");
    console.log("isAdmin:", isAdmin);
    console.log("isWorker:", isWorker);

    const messagesRef = collection(db, 'messages');
    let q;
    
    if (isAdmin || isWorker) {
      // Workers and admins can see all messages
      q = query(messagesRef, orderBy('createdAt', 'desc'));
    } else {
      // Regular users only see their own messages
      q = query(
        messagesRef, 
        where('userId', '==', auth.currentUser?.uid),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messageList = snapshot.docs.map((doc) => {
        const timestamp = doc.data().createdAt; 
        console.log("Timestamp:", timestamp); // Log the timestamp object
    
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: timestamp ? timestamp.toDate() : null, 
        };
      });
      setMessages(messageList);
    });

    return unsubscribe;
  }, [isAdmin, isWorker, auth.currentUser?.uid]);

  useEffect(() => {
    if (selectedMessage) {
      const messageRef = doc(db, 'messages', selectedMessage.id);
      const unsubscribe = onSnapshot(messageRef, (doc) => {
        if (doc.exists()) {
          setSelectedMessage({ id: doc.id, ...doc.data() });
        }
      });
  
      return () => unsubscribe();
    }
  }, [selectedMessage?.id]); 

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
  
    console.log("Attempting to send message...");
    console.log("Current user:", auth.currentUser);
  
    try {
      const newDoc = await addDoc(collection(db, 'messages'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        message: newMessage,
        createdAt: serverTimestamp(),
        status: 'open',
        responses: []
      });
  
      // List of admin email addresses
      const adminEmails = ['bramesar5@gmail.com', 'neymardineroo@gmail.com'];  // Replace with actual admin emails
  
      // Loop through the admin emails and send the email to each one
      for (const adminEmail of adminEmails) {
        const functions = getFunctions();
        const sendEmail = httpsCallable(functions, 'sendEmail');
        
        await sendEmail({
          to: adminEmail,  // Admin email
          subject: 'New Support Message from ' + auth.currentUser.email,
          html: `<p><strong>Message:</strong> ${newMessage}</p><p><strong>From:</strong> ${auth.currentUser.email}</p>`
        });
      }
  
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message or email:', error);
    }
  };
  
  
  


const sendResponse = async (messageId, responseText) => {
  console.log("Attempting to send response:", { messageId, responseText });
  if (!responseText.trim()) return;

  try {
    const messageRef = doc(db, 'messages', messageId);
    
    // Get current message data
    const messageSnapshot = await getDoc(messageRef);
    const currentMessage = messageSnapshot.data();
    const currentResponses = currentMessage.responses || [];

    const newResponse = {
      responderUid: auth.currentUser.uid,
      responderEmail: auth.currentUser.email,
      response: responseText,
      createdAt: new Date().toISOString()  // Use ISO string instead of serverTimestamp
    };

    console.log("Updating with new response:", newResponse);

    await updateDoc(messageRef, {
      responses: [...currentResponses, newResponse]
    });
    
    console.log("Response sent successfully");
  } catch (error) {
    console.error('Error sending response:', error);
  }
};



  

  return (
    <div className="max-w-lg mx-auto h-[600px] flex flex-col bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="text-center font-semibold text-xl bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
          {isAdmin || isWorker ? 'Support Messages' : 'Message Support'}
        </h2>
      </div>

      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg) => (
            <div 
              key={msg.id} 
              onClick={() => {
                setSelectedMessage(msg);
                setIsChatOpen(true);
              }}
              className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors border"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{msg.userEmail}</p>
                  <p className="text-gray-600 truncate">{msg.message}</p>
                </div>
                <p className="text-xs text-gray-400">
                  {msg.createdAt?.toDate?.() 
                    ? msg.createdAt.toDate().toLocaleString() 
                    : new Date().toLocaleString()}
                </p>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {msg.responses?.length || 0} responses
              </div>
            </div>
        ))}
      </div>

      
  
      {/* Message input for regular users */}
      {(!isAdmin && !isWorker) && (
        <div className="p-4 border-t">
          <form onSubmit={sendMessage} className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1 px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Type a message..."
            />
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-full p-2 w-10 h-10 flex items-center justify-center hover:opacity-90 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
              </svg>
            </button>
          </form>
        </div>
      )}

        
      {/* Chat Modal */}
      {selectedMessage && (
        <ChatModal
          isOpen={isChatOpen}
          onClose={() => {
            setIsChatOpen(false);
            setSelectedMessage(null);
          }}
          selectedChat={selectedMessage}
          onSendResponse={sendResponse}
          responderEmail={auth.currentUser?.email}
        />
      )}


      
    </div>
  );
};

export default Messages;